import { Component, OnInit, ElementRef, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ExternalDataService,
  Zone,
  PricingGroup,
  BookingConflict,
  UpsertZoneDto,
  readBookingConflict,
} from '../../services/externalDataService.service';
import { DistrictService, DistrictDto } from '../../services/district.service';
import { CC_PERMISSIONS } from '../../services/permission.service';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { BookingConflictPanelComponent } from '../shared/booking-conflict-panel/booking-conflict-panel.component';
import { ZoneEditorComponent, ZoneEditorValue } from './zone-editor/zone-editor.component';
import { environment } from '../../../environments/environment';

interface ZoneDisplay extends Zone {
  visible: boolean;
  polygon?: google.maps.Polygon;
  infoWindow?: google.maps.InfoWindow;
  bounds?: google.maps.LatLngBounds;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DISABLED_ZONE_COLOR = '#9CA3AF';

@Component({
  selector: 'app-geo-zone-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HasPermissionDirective,
    BookingConflictPanelComponent,
    ZoneEditorComponent,
  ],
  templateUrl: './geo-zone-manager.component.html',
  styleUrl: './geo-zone-manager.component.css',
})
export class GeoZoneManagerComponent implements OnInit {
  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  readonly P = CC_PERMISSIONS;

  private map: google.maps.Map | null = null;
  zones: ZoneDisplay[] = [];
  filteredZones: ZoneDisplay[] = [];
  pricingGroups: PricingGroup[] = [];
  districts: DistrictDto[] = [];
  timeSlots: string[] = [];
  loading = false;
  searchTerm = '';
  selectedZone: ZoneDisplay | null = null;
  isEditing = false;
  isCreating = false;
  saving = false;
  togglingStatus = false;
  pointCount = 0;
  editConflict: BookingConflict | null = null;
  statusConflict: BookingConflict | null = null;
  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info',
  };

  private mapClickListener: google.maps.MapsEventListener | null = null;
  private draftPolygon: google.maps.Polygon | null = null;
  private pathListeners: google.maps.MapsEventListener[] = [];
  private notificationTimer: ReturnType<typeof setTimeout> | null = null;

  // Map control states
  mapTypeId: 'roadmap' | 'satellite' | 'hybrid' | 'terrain' = 'roadmap';
  showLabels = true;
  showTraffic = false;
  private trafficLayer: google.maps.TrafficLayer | null = null;

  // Zone colors for visual distinction
  readonly zoneColors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E2',
    '#F8B739',
    '#52B788',
  ];

  constructor(
    private externalDataService: ExternalDataService,
    private ngZone: NgZone,
    private districtService: DistrictService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadMapAndZones();
    this.loadPricingGroups();
    this.loadDistricts();
    this.loadTimeSlots();
  }

  private loadTimeSlots(): void {
    this.externalDataService.getGlobalSettings().subscribe({
      next: (settings) => {
        this.timeSlots = settings?.appointmentTimeSlots ?? [];
      },
      error: (error) => {
        console.error('Error loading time slots:', error);
      },
    });
  }

  private loadPricingGroups(): void {
    this.externalDataService.getPricingGroups().subscribe({
      next: (data) => {
        this.pricingGroups = data;
      },
      error: (error) => {
        console.error('Error loading pricing groups:', error);
      }
    });
  }

  private loadDistricts(): void {
    this.districtService.getDistricts().subscribe({
      next: (data) => {
        this.districts = data;
      },
      error: (error) => {
        console.error('Error loading districts:', error);
      }
    });
  }

  private async loadMapAndZones(): Promise<void> {
    this.loading = true;
    try {
      await this.loadGoogleMapsApi(environment.googleMapsApiKey);
      this.initializeMap();
      await this.loadZones();
    } catch (error) {
      console.error('Error loading map and zones:', error);
      this.showNotification('Failed to load map or zones', 'error');
    } finally {
      this.loading = false;
    }
  }

  private loadGoogleMapsApi(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.maps) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Google Maps JS API'));
      document.head.appendChild(script);
    });
  }

  private initializeMap(): void {
    if (!this.mapContainer) return;

    // Center on Kerala, India (default location)
    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: { lat: 8.5241, lng: 76.9366 }, // Trivandrum, Kerala
      zoom: 10,
      mapTypeId: this.mapTypeId,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      streetViewControl: true,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      scaleControl: true,
    });

    // Initialize traffic layer
    this.trafficLayer = new google.maps.TrafficLayer();
  }

  private async loadZones(fitToZones = true): Promise<void> {
    this.externalDataService.getAllZones().subscribe({
      next: (data) => {
        // Clear polygons from a previous load before re-rendering.
        this.zones.forEach((zone) => zone.polygon?.setMap(null));
        this.zones = data.map((zone) => ({
          ...zone,
          visible: true,
        }));
        this.onSearchChange();
        this.renderAllZones();
        if (fitToZones) {
          this.fitMapToZones();
        }
        if (this.selectedZone) {
          this.selectedZone =
            this.zones.find((z) => z.id === this.selectedZone?.id) ?? null;
        }
      },
      error: (error) => {
        console.error('Error loading zones:', error);
        this.showNotification('Failed to load zones', 'error');
      },
    });
  }

  private renderAllZones(): void {
    if (!this.map) return;

    this.zones.forEach((zone, index) => {
      this.renderZone(zone, index);
    });
  }

  private renderZone(zone: ZoneDisplay, colorIndex: number): void {
    if (
      !this.map ||
      !zone.coordinate?.coordinates ||
      zone.coordinate.coordinates.length === 0
    ) {
      return;
    }

    // Convert coordinates to Google Maps format
    const paths = zone.coordinate.coordinates.map((coord) => ({
      lat: coord.latitude,
      lng: coord.longitude,
    }));

    // Create polygon (disabled zones are drawn grey)
    const color = this.getZoneColor(zone, colorIndex);
    const polygon = new google.maps.Polygon({
      paths: paths,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: color,
      fillOpacity: zone.disabled ? 0.12 : 0.25,
      map: zone.visible ? this.map : null,
      clickable: true,
      draggable: false,
      editable: false,
    });

    // Calculate zone center and bounds
    const bounds = new google.maps.LatLngBounds();
    paths.forEach((path) => bounds.extend(path));
    zone.bounds = bounds;

    // Create info window
    const infoWindow = new google.maps.InfoWindow();

    // Add click listener
    polygon.addListener('click', (event: google.maps.MapMouseEvent) => {
      const center = bounds.getCenter();
      const area = google.maps.geometry.spherical.computeArea(
        polygon.getPath(),
      );
      const areaInKm = (area / 1000000).toFixed(2);
      const coordCount = zone.coordinate?.coordinates?.length || 0;

      infoWindow.setContent(`
        <div style="padding: 8px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; color: #1a73e8; font-size: 16px; font-weight: 600;">${zone.name}</h3>
          <div style="font-size: 13px; color: #5f6368; line-height: 1.6;">
            <p style="margin: 4px 0;"><strong>Zone ID:</strong> ${zone.id}</p>
            <p style="margin: 4px 0;"><strong>Area:</strong> ~${areaInKm} km²</p>
            <p style="margin: 4px 0;"><strong>Coordinates:</strong> ${coordCount} points</p>
            <p style="margin: 4px 0;"><strong>Created:</strong> ${new Date(zone.createdAt).toLocaleDateString()}</p>
            ${zone.disabled ? '<p style="margin: 4px 0; color: #d93025;"><strong>Status:</strong> Disabled</p>' : ''}
          </div>
        </div>
      `);
      infoWindow.setPosition(event.latLng || center);
      infoWindow.open(this.map!);
    });

    // Add hover effects
    polygon.addListener('mouseover', () => {
      polygon.setOptions({
        strokeWeight: 3,
        fillOpacity: 0.4,
      });
    });

    polygon.addListener('mouseout', () => {
      polygon.setOptions({
        strokeWeight: 2,
        fillOpacity: zone.disabled ? 0.12 : 0.25,
      });
    });

    zone.polygon = polygon;
    zone.infoWindow = infoWindow;
  }

  private fitMapToZones(): void {
    if (!this.map || this.zones.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    let hasCoordinates = false;

    this.zones.forEach((zone) => {
      if (zone.coordinate?.coordinates) {
        zone.coordinate.coordinates.forEach((coord) => {
          bounds.extend({ lat: coord.latitude, lng: coord.longitude });
          hasCoordinates = true;
        });
      }
    });

    if (hasCoordinates) {
      this.map.fitBounds(bounds);
      // Limit zoom level
      const listener = google.maps.event.addListener(this.map, 'idle', () => {
        const zoom = this.map!.getZoom();
        if (zoom && zoom > 14) {
          this.map!.setZoom(14);
        }
        google.maps.event.removeListener(listener);
      });
    }
  }

  // Search and filter functionality
  onSearchChange(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredZones = [...this.zones];
    } else {
      this.filteredZones = this.zones.filter(
        (zone) =>
          zone.name.toLowerCase().includes(term) ||
          zone.id.toLowerCase().includes(term),
      );
    }
  }

  // Zone visibility toggle
  toggleZoneVisibility(zone: ZoneDisplay): void {
    zone.visible = !zone.visible;
    if (zone.polygon) {
      zone.polygon.setMap(zone.visible ? this.map : null);
    }
  }

  // Show all zones
  showAllZones(): void {
    this.zones.forEach((zone) => {
      zone.visible = true;
      if (zone.polygon) {
        zone.polygon.setMap(this.map);
      }
    });
  }

  // Hide all zones
  hideAllZones(): void {
    this.zones.forEach((zone) => {
      zone.visible = false;
      if (zone.polygon) {
        zone.polygon.setMap(null);
      }
    });
  }

  // Zoom to specific zone
  zoomToZone(zone: ZoneDisplay, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.map || !zone.bounds) return;
    this.map.fitBounds(zone.bounds);
    this.selectedZone = zone;
    this.leaveEditModes();

    // Highlight the zone briefly without zoom animation
    if (zone.polygon) {
      const originalStrokeWeight = zone.polygon.get('strokeWeight');
      const originalFillOpacity = zone.polygon.get('fillOpacity');

      zone.polygon.setOptions({
        strokeWeight: 4,
        fillOpacity: 0.5,
      });

      setTimeout(() => {
        zone.polygon?.setOptions({
          strokeWeight: originalStrokeWeight,
          fillOpacity: originalFillOpacity,
        });
      }, 1500);
    }
  }

  // Select zone without zooming (for clicking on the card)
  selectZone(zone: ZoneDisplay, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedZone = zone;
    this.leaveEditModes();

    // Just highlight without zooming
    if (zone.polygon) {
      const originalStrokeWeight = zone.polygon.get('strokeWeight');
      const originalFillOpacity = zone.polygon.get('fillOpacity');

      zone.polygon.setOptions({
        strokeWeight: 4,
        fillOpacity: 0.5,
      });

      setTimeout(() => {
        zone.polygon?.setOptions({
          strokeWeight: originalStrokeWeight,
          fillOpacity: originalFillOpacity,
        });
      }, 1500);
    }
  }

  // Map type change
  changeMapType(type: 'roadmap' | 'satellite' | 'hybrid' | 'terrain'): void {
    this.mapTypeId = type;
    if (this.map) {
      this.map.setMapTypeId(type);
    }
  }

  // Toggle traffic layer
  toggleTraffic(): void {
    this.showTraffic = !this.showTraffic;
    if (this.trafficLayer) {
      this.trafficLayer.setMap(this.showTraffic ? this.map : null);
    }
  }

  // Export functionality
  exportZones(format: 'json' | 'geojson' | 'kml' | 'csv'): void {
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'json':
        content = JSON.stringify(this.zones, null, 2);
        filename = `geo-zones-${this.getTimestamp()}.json`;
        mimeType = 'application/json';
        break;

      case 'geojson':
        content = this.convertToGeoJSON();
        filename = `geo-zones-${this.getTimestamp()}.geojson`;
        mimeType = 'application/geo+json';
        break;

      case 'kml':
        content = this.convertToKML();
        filename = `geo-zones-${this.getTimestamp()}.kml`;
        mimeType = 'application/vnd.google-earth.kml+xml';
        break;

      case 'csv':
        content = this.convertToCSV();
        filename = `geo-zones-${this.getTimestamp()}.csv`;
        mimeType = 'text/csv';
        break;

      default:
        return;
    }

    this.downloadFile(content, filename, mimeType);
  }

  private convertToGeoJSON(): string {
    const features = this.zones.map((zone) => ({
      type: 'Feature',
      properties: {
        id: zone.id,
        name: zone.name,
        disabled: zone.disabled,
        createdAt: zone.createdAt,
        updatedAt: zone.updatedAt,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [
          zone.coordinate?.coordinates.map((coord) => [
            coord.longitude,
            coord.latitude,
          ]) || [],
        ],
      },
    }));

    return JSON.stringify(
      {
        type: 'FeatureCollection',
        features: features,
      },
      null,
      2,
    );
  }

  private convertToKML(): string {
    let kml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    kml += '<kml xmlns="http://www.opengis.net/kml/2.2">\n';
    kml += '  <Document>\n';
    kml += '    <name>ClearScrap Geo Zones</name>\n';
    kml +=
      '    <description>Exported geo zones from ClearScrap Control Center</description>\n';

    this.zones.forEach((zone, index) => {
      const color = this.zoneColors[index % this.zoneColors.length].replace(
        '#',
        '',
      );
      kml += '    <Style id="zone' + index + '">\n';
      kml += '      <LineStyle>\n';
      kml += '        <color>cc' + color + '</color>\n';
      kml += '        <width>2</width>\n';
      kml += '      </LineStyle>\n';
      kml += '      <PolyStyle>\n';
      kml += '        <color>40' + color + '</color>\n';
      kml += '      </PolyStyle>\n';
      kml += '    </Style>\n';
      kml += '    <Placemark>\n';
      kml += '      <name>' + this.escapeXml(zone.name) + '</name>\n';
      kml +=
        '      <description>' + this.escapeXml(zone.id) + '</description>\n';
      kml += '      <styleUrl>#zone' + index + '</styleUrl>\n';
      kml += '      <Polygon>\n';
      kml += '        <outerBoundaryIs>\n';
      kml += '          <LinearRing>\n';
      kml += '            <coordinates>\n';

      if (zone.coordinate?.coordinates) {
        zone.coordinate.coordinates.forEach((coord) => {
          kml += `              ${coord.longitude},${coord.latitude},0\n`;
        });
      }

      kml += '            </coordinates>\n';
      kml += '          </LinearRing>\n';
      kml += '        </outerBoundaryIs>\n';
      kml += '      </Polygon>\n';
      kml += '    </Placemark>\n';
    });

    kml += '  </Document>\n';
    kml += '</kml>';
    return kml;
  }

  private convertToCSV(): string {
    let csv =
      'Zone ID,Zone Name,Status,Created Date,Coordinate Count,Coordinates (Lat,Lng)\n';

    this.zones.forEach((zone) => {
      const coords = zone.coordinate?.coordinates || [];
      const coordStr = coords
        .map((c) => `${c.latitude},${c.longitude}`)
        .join(';');
      csv += `"${zone.id}","${zone.name}","${zone.disabled ? 'Disabled' : 'Active'}","${zone.createdAt}",${coords.length},"${coordStr}"\n`;
    });

    return csv;
  }

  private escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private downloadFile(
    content: string,
    filename: string,
    mimeType: string,
  ): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getTimestamp(): string {
    return new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  }

  // Get zone statistics
  getZoneStats() {
    return {
      total: this.zones.length,
      visible: this.zones.filter((z) => z.visible).length,
      active: this.zones.filter((z) => !z.disabled).length,
      disabled: this.zones.filter((z) => z.disabled).length,
    };
  }

  // Check for overlapping zones
  checkOverlaps(): void {
    if (!this.map || this.zones.length < 2) {
      this.showNotification('Need at least 2 zones to check for overlaps', 'info');
      return;
    }

    const overlaps: Array<{ zone1: string; zone2: string }> = [];

    for (let i = 0; i < this.zones.length; i++) {
      for (let j = i + 1; j < this.zones.length; j++) {
        const zone1 = this.zones[i];
        const zone2 = this.zones[j];

        if (
          zone1.polygon &&
          zone2.polygon &&
          this.polygonsOverlap(zone1.polygon, zone2.polygon)
        ) {
          overlaps.push({
            zone1: zone1.name,
            zone2: zone2.name,
          });
        }
      }
    }

    if (overlaps.length === 0) {
      this.showNotification(
        'No overlapping zones detected. All zones are properly separated.',
        'success',
      );
    } else {
      const message =
        `Found ${overlaps.length} overlapping zone pair(s): ` +
        overlaps.map((o) => `${o.zone1} ↔ ${o.zone2}`).join(', ');
      this.showNotification(message, 'info');
    }
  }

  /** True when two polygons share area: a vertex of one lies inside the other, or edges cross. */
  private polygonsOverlap(
    polygon1: google.maps.Polygon,
    polygon2: google.maps.Polygon,
  ): boolean {
    const path1 = polygon1.getPath();
    const path2 = polygon2.getPath();

    for (let k = 0; k < path2.getLength(); k++) {
      if (google.maps.geometry.poly.containsLocation(path2.getAt(k), polygon1)) {
        return true;
      }
    }

    for (let k = 0; k < path1.getLength(); k++) {
      if (google.maps.geometry.poly.containsLocation(path1.getAt(k), polygon2)) {
        return true;
      }
    }

    return this.checkEdgeIntersections(path1, path2);
  }

  // Check if polygon edges intersect
  private checkEdgeIntersections(
    path1: google.maps.MVCArray<google.maps.LatLng>,
    path2: google.maps.MVCArray<google.maps.LatLng>,
  ): boolean {
    for (let i = 0; i < path1.getLength(); i++) {
      const a1 = path1.getAt(i);
      const a2 = path1.getAt((i + 1) % path1.getLength());

      for (let j = 0; j < path2.getLength(); j++) {
        const b1 = path2.getAt(j);
        const b2 = path2.getAt((j + 1) % path2.getLength());

        if (this.lineSegmentsIntersect(a1, a2, b1, b2)) {
          return true;
        }
      }
    }
    return false;
  }

  // Check if two line segments intersect
  private lineSegmentsIntersect(
    a1: google.maps.LatLng,
    a2: google.maps.LatLng,
    b1: google.maps.LatLng,
    b2: google.maps.LatLng,
  ): boolean {
    const ccw = (
      A: google.maps.LatLng,
      B: google.maps.LatLng,
      C: google.maps.LatLng,
    ): boolean => {
      return (
        (C.lng() - A.lng()) * (B.lat() - A.lat()) >
        (B.lng() - A.lng()) * (C.lat() - A.lat())
      );
    };

    return (
      ccw(a1, b1, b2) !== ccw(a2, b1, b2) && ccw(a1, a2, b1) !== ccw(a1, a2, b2)
    );
  }

  // ── Create / edit ──

  get editorOpen(): boolean {
    return this.isEditing || this.isCreating;
  }

  /**
   * Starts drawing a new zone: each map click adds a boundary point to an editable draft polygon.
   * Uses only the core Maps API (the Drawing Library is deprecated and being removed by Google).
   */
  startCreate(): void {
    if (!this.map) return;
    this.leaveEditModes();
    this.selectedZone = null;
    this.isCreating = true;
    this.editConflict = null;

    // An empty `paths: []` (or a bare empty MVCArray) leaves the polygon with no path at all, so
    // getPath() returns undefined; wrap an empty MVCArray in an array to get an extendable path.
    this.draftPolygon = new google.maps.Polygon({
      map: this.map,
      paths: [new google.maps.MVCArray<google.maps.LatLng>()],
      strokeColor: '#16A34A',
      strokeWeight: 2,
      fillColor: '#16A34A',
      fillOpacity: 0.25,
      editable: true,
      zIndex: 1000,
    });
    this.watchPath(this.draftPolygon);

    // Let clicks over existing zones reach the map so points can be placed anywhere.
    this.setZonePolygonsClickable(false);
    this.map.setOptions({ draggableCursor: 'crosshair' });
    this.mapClickListener = this.map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          this.draftPolygon?.getPath().push(event.latLng);
        }
      },
    );
  }

  /** Removes the last boundary point of the zone being drawn. */
  undoLastPoint(): void {
    const path = this.draftPolygon?.getPath();
    if (path && path.getLength() > 0) {
      path.pop();
    }
  }

  /** Removes all boundary points of the zone being drawn. */
  clearDraft(): void {
    this.draftPolygon?.getPath().clear();
  }

  /** Edits the selected zone: form fields plus draggable boundary vertices. */
  startEditing(): void {
    if (!this.selectedZone) return;
    this.statusConflict = null;
    this.editConflict = null;
    this.isEditing = true;
    const polygon = this.selectedZone.polygon;
    if (polygon) {
      polygon.setEditable(true);
      this.watchPath(polygon);
    } else {
      this.pointCount = 0;
    }
  }

  cancelEditing(): void {
    this.leaveEditModes();
  }

  onEditorSave(value: ZoneEditorValue): void {
    if (this.saving) return;
    const polygon = this.isCreating
      ? this.draftPolygon
      : this.selectedZone?.polygon;
    if (!polygon) {
      this.showNotification('Draw the zone on the map first.', 'error');
      return;
    }

    const overlapping = this.zones
      .filter(
        (z) => z.id !== this.selectedZone?.id && !!z.polygon && !z.disabled,
      )
      .filter((z) => this.polygonsOverlap(polygon, z.polygon!))
      .map((z) => z.name);
    if (
      overlapping.length > 0 &&
      !confirm(
        `This boundary overlaps ${overlapping.join(', ')}. Bookings in the overlap may go to either zone. Save anyway?`,
      )
    ) {
      return;
    }

    const payload: UpsertZoneDto = {
      ...value,
      coordinates: polygon
        .getPath()
        .getArray()
        .map((p) => ({ latitude: p.lat(), longitude: p.lng() })),
    };

    this.saving = true;
    this.editConflict = null;
    const request$ = this.isCreating
      ? this.externalDataService.createZone(payload)
      : this.externalDataService.updateZone(this.selectedZone!.id, payload);

    request$.subscribe({
      next: (response: { id: string }) => {
        const wasCreating = this.isCreating;
        this.saving = false;
        this.leaveEditModes(false);
        this.selectedZone = { id: response.id } as ZoneDisplay;
        this.loadZones(false);
        this.showNotification(
          wasCreating
            ? `Zone "${value.name}" created.`
            : `Zone "${value.name}" updated.`,
          'success',
        );
      },
      error: (error) => {
        console.error('Error saving zone:', error);
        this.saving = false;
        this.editConflict = readBookingConflict(error);
        if (!this.editConflict) {
          this.showNotification(
            this.errorMessage(error, 'Failed to save zone'),
            'error',
          );
        }
      },
    });
  }

  /** Enables or disables the selected zone (disabled zones take no new bookings). */
  toggleZoneStatus(): void {
    const zone = this.selectedZone;
    if (!zone || this.togglingStatus) return;
    const enable = !!zone.disabled;
    const question = enable
      ? `Enable "${zone.name}"? Customers will be able to book in this zone again.`
      : `Disable "${zone.name}"? Customers will see "temporarily unavailable" and cannot book here.`;
    if (!confirm(question)) return;

    this.togglingStatus = true;
    this.statusConflict = null;
    this.externalDataService.setZoneStatus(zone.id, enable).subscribe({
      next: (updated) => {
        this.togglingStatus = false;
        zone.disabled = updated.disabled;
        const color = this.getZoneColor(zone, this.zones.indexOf(zone));
        zone.polygon?.setOptions({
          strokeColor: color,
          fillColor: color,
          fillOpacity: zone.disabled ? 0.12 : 0.25,
        });
        this.showNotification(
          `Zone "${zone.name}" ${enable ? 'enabled' : 'disabled'}.`,
          'success',
        );
      },
      error: (error) => {
        console.error('Error changing zone status:', error);
        this.togglingStatus = false;
        this.statusConflict = readBookingConflict(error);
        if (!this.statusConflict) {
          this.showNotification(
            this.errorMessage(error, 'Failed to change zone status'),
            'error',
          );
        }
      },
    });
  }

  getScheduleSummary(zone: Zone): string {
    const schedule = zone.serviceSchedule ?? [];
    if (schedule.length === 0) {
      return 'Every day';
    }
    return [...schedule]
      .sort((a, b) => ((a.day + 6) % 7) - ((b.day + 6) % 7))
      .map((d) =>
        d.timeSlots && d.timeSlots.length > 0
          ? `${DAY_SHORT[d.day]} (${d.timeSlots.join(', ')})`
          : DAY_SHORT[d.day],
      )
      .join(', ');
  }

  getZoneColor(zone: Zone, index: number): string {
    return zone.disabled
      ? DISABLED_ZONE_COLOR
      : this.zoneColors[Math.max(index, 0) % this.zoneColors.length];
  }

  showNotification(
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
  ): void {
    this.notification = { show: true, message, type };
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer);
    }
    this.notificationTimer = setTimeout(() => {
      this.notification.show = false;
    }, 5000);
  }

  dismissNotification(): void {
    this.notification.show = false;
  }

  /**
   * Leaves create/edit mode. When `restoreShape` is true the edited zone's boundary is put back
   * to its saved coordinates (a cancelled edit); a draft polygon is always removed.
   */
  private leaveEditModes(restoreShape = true): void {
    this.pathListeners.forEach((listener) => listener.remove());
    this.pathListeners = [];

    if (this.isEditing && this.selectedZone?.polygon) {
      const polygon = this.selectedZone.polygon;
      polygon.setEditable(false);
      if (restoreShape) {
        polygon.setPath(
          (this.selectedZone.coordinate?.coordinates ?? []).map((c) => ({
            lat: c.latitude,
            lng: c.longitude,
          })),
        );
      }
    }

    this.draftPolygon?.setMap(null);
    this.draftPolygon = null;
    if (this.mapClickListener) {
      this.mapClickListener.remove();
      this.mapClickListener = null;
      this.setZonePolygonsClickable(true);
      this.map?.setOptions({ draggableCursor: null });
    }
    this.isEditing = false;
    this.isCreating = false;
    this.editConflict = null;
    this.pointCount = 0;
  }

  private setZonePolygonsClickable(clickable: boolean): void {
    this.zones.forEach((zone) => zone.polygon?.setOptions({ clickable }));
  }

  /** Keeps pointCount in sync while vertices are added, moved or removed. */
  private watchPath(polygon: google.maps.Polygon): void {
    this.pathListeners.forEach((listener) => listener.remove());
    const path = polygon.getPath();
    // Maps MVC events can fire outside Angular's zone; re-enter it so the count re-renders.
    const update = () =>
      this.ngZone.run(() => (this.pointCount = path.getLength()));
    this.pathListeners = [
      path.addListener('insert_at', update),
      path.addListener('set_at', update),
      path.addListener('remove_at', update),
    ];
    update();
  }

  private errorMessage(error: any, fallback: string): string {
    const validation = error?.error?.errors;
    if (Array.isArray(validation) && validation.length > 0) {
      return validation.join(' ');
    }
    return error?.error?.detail || fallback;
  }

  getPricingGroupName(pricingGroupId: string | null | undefined): string {
    if (!pricingGroupId || pricingGroupId === 'null' || pricingGroupId === 'undefined') return 'Global';
    const group = this.pricingGroups.find(g => g.id === pricingGroupId);
    return group ? group.name : 'Unknown Group';
  }

  getDistrictName(districtId: string | null | undefined): string {
    if (!districtId || districtId === 'null' || districtId === 'undefined') return 'Unassigned';
    const district = this.districts.find(d => d.id === districtId);
    return district ? district.name : 'Unknown District';
  }
}
